import React from 'react';


export default function PublicTraining() {
  

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

      {/* Header */}
      <div className="mb-10">
        <h1 className="font-display text-3xl text-navy-900 mb-3">
          Connecting With Your Leads
        </h1>
        <p className="text-navy-600 font-body text-lg leading-relaxed">
          This lesson is from the{' '}
          <a 
            href="https://join.thedeolixacademy.com/luxury-travel-advisor" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-coral-500 hover:text-coral-600 underline"
          >
            Become a Luxury Travel Advisor
          </a>{' '}
          training program by The Deolix Academy. It covers the 5-DIME Sequence (the five "Don't Ignore Me Emails"), a proven follow-up system designed to get your internet leads to actually respond.
        </p>
      </div>

      {/* Intro Card */}
      <div className="bg-white rounded-xl shadow-luxury p-6 sm:p-8 mb-8">
        <h2 className="font-display text-xl text-navy-900 mb-4">What's This About?</h2>
        <div className="space-y-4 text-navy-700 font-body leading-relaxed">
          <p>
            When you receive an internet lead, the biggest challenge isn't the quality of the lead. It's getting them to respond to you. These are people who've actively requested help planning a luxury trip, but they're busy, they're getting distracted, and your email is competing with everything else in their inbox.
          </p>
          <p>
            The 5-DIME Sequence is a structured five-email follow-up system that solves this problem. DIME stands for "Don't Ignore Me Email." It's a series of five emails, each with a specific purpose, timed at deliberate intervals to maximize your chances of getting a response without being pushy.
          </p>
          <p>
            You don't have to use this exact system. If you've already developed your own follow-up process that works for you, that's great. The key takeaway is that you need at least five sequential touchpoints with every lead. One email isn't enough. Two isn't enough. The data from hundreds of advisors consistently shows that most people respond by the third email, but some need all five. The advisors who give up after one or two emails are leaving money on the table.
          </p>
        </div>
      </div>

      {/* Video Section */}
      <div className="bg-white rounded-xl shadow-luxury p-6 sm:p-8 mb-8">
        <h2 className="font-display text-xl text-navy-900 mb-4">Watch the Lesson</h2>
        <div style={{ padding: '75% 0 0 0', position: 'relative' }} className="rounded-lg overflow-hidden mb-6">
          <iframe 
            src="https://player.vimeo.com/video/1192543745?badge=0&autopause=0&player_id=0&app_id=58479" 
            frameBorder="0" 
            allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share" 
            referrerPolicy="strict-origin-when-cross-origin" 
            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }} 
            title="Let's Connect - 5-DIME Sequence"
          />
        </div>
      </div>

      {/* Summary Section */}
      <div className="bg-white rounded-xl shadow-luxury p-6 sm:p-8 mb-8">
        <h2 className="font-display text-xl text-navy-900 mb-4">Video Summary</h2>
        <div className="space-y-4 text-navy-700 font-body leading-relaxed">
          <p>
            This lesson covers the "Let's Connect" step of the Trust Conversion System. The sole purpose of this step is to get the lead to book a proper conversation with you, nothing more. It sounds simple, but when you're working with cold internet leads, up to 50% of people who reach out may never respond to your first email.
          </p>
          <p>
            The reason? By the time you get back to them, they're caught up in their day. They were in the mood to plan a trip when they sent the inquiry, but that mood has passed. The 5-DIME Sequence is built to solve this. It's five messages, timed at specific intervals, each with a distinct purpose.
          </p>

          <div className="bg-cream-50 rounded-lg p-5 space-y-3">
            <div className="flex items-start gap-3">
              <span className="flex-shrink-0 w-7 h-7 rounded-full bg-coral-500 text-white text-sm font-bold flex items-center justify-center mt-0.5">1</span>
              <div>
                <span className="font-medium text-navy-900">The Invitation</span>
                <span className="text-navy-600"> — sent immediately. Light and conversational. Confirms you can help and offers a Calendly link or a simple reply to schedule a quick conversation. No big commitment.</span>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="flex-shrink-0 w-7 h-7 rounded-full bg-coral-500 text-white text-sm font-bold flex items-center justify-center mt-0.5">2</span>
              <div>
                <span className="font-medium text-navy-900">The Follow-Up</span>
                <span className="text-navy-600"> — 6 to 12 hours later. A gentle reminder that you reached out. Keep it short. If they provided a phone number, try calling them shortly before sending this one.</span>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="flex-shrink-0 w-7 h-7 rounded-full bg-coral-500 text-white text-sm font-bold flex items-center justify-center mt-0.5">3</span>
              <div>
                <span className="font-medium text-navy-900">The Example</span>
                <span className="text-navy-600"> — 24 hours later. This is where you reignite their excitement. Instead of another follow-up, share something specific and compelling, like five unique private tours in their destination. Use AI to help you create content that sparks their imagination.</span>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="flex-shrink-0 w-7 h-7 rounded-full bg-coral-500 text-white text-sm font-bold flex items-center justify-center mt-0.5">4</span>
              <div>
                <span className="font-medium text-navy-900">The Reminder</span>
                <span className="text-navy-600"> — 24 hours later. References yesterday's email, then reminds them of the specific benefits of working with you: customized itineraries, VIP access, private local guides, and a specialist dedicated to their trip.</span>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="flex-shrink-0 w-7 h-7 rounded-full bg-coral-500 text-white text-sm font-bold flex items-center justify-center mt-0.5">5</span>
              <div>
                <span className="font-medium text-navy-900">Should I Stay?</span>
                <span className="text-navy-600"> — 24 hours later. The subject line "Should I Stay or Should I Go?" is so unusual it virtually guarantees they'll open it. Inside, they pick from three numbered options: not interested, ready to book, or plans changed. This approach has achieved a near 100% response rate.</span>
              </div>
            </div>
          </div>

          <p>
            The video also covers why you should always send a written response first (even if they provided a phone number), how to use Calendly to remove scheduling friction, the importance of personalizing each message with the lead's specific details, and how this same sequence can be reused later in the process if a client goes quiet after receiving a draft itinerary.
          </p>
          <p>
            The key insight throughout: your goal is to get them into a live conversation, whether phone or video. You can't build trust through email alone, and you definitely can't do a proper discovery call over text. Every message in this sequence is designed to move them one step closer to that real-time connection.
          </p>

          <div className="bg-blue-50 rounded-lg p-5 border border-blue-200">
            <p className="text-blue-900 font-medium mb-2">What if they still don't respond?</p>
            <p className="text-blue-800 text-sm">
              If you've sent all five messages and haven't heard back, don't write them off. Add them to your CRM and continue to reach out periodically, the same way you would with any past client. Remember, you've paid for this lead and they're yours. People's circumstances change. Someone who didn't respond today might come back to you six months or a year from now when the timing is right. Just because they didn't engage immediately doesn't mean they never will.
            </p>
          </div>
        </div>
      </div>

      {/* Download Section */}
      <div className="bg-white rounded-xl shadow-luxury p-6 sm:p-8 mb-8">
        <h2 className="font-display text-xl text-navy-900 mb-4">Download the Templates</h2>
        <p className="text-navy-700 font-body leading-relaxed mb-5">
          The Word document below contains all five email templates from the 5-DIME Sequence, ready to customize with your own details. Use them as starting points and adjust the language to match your personal style.
        </p>
        <a
          href="/5-DIME-Sequence-Templates.docx"
          download
          className="inline-flex items-center gap-2 btn-primary"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          Download 5-DIME Email Templates (.docx)
        </a>
      </div>

      {/* Upsell Section */}
      <div className="bg-gradient-to-br from-navy-900 to-navy-800 rounded-xl shadow-luxury p-6 sm:p-8 text-white">
        <h2 className="font-display text-xl mb-3">Want the Full Training Program?</h2>
        <p className="font-body text-navy-200 leading-relaxed mb-4">
          This lesson is just one small piece of the{' '}
          <span className="text-white font-medium">Become a Luxury Travel Advisor</span>{' '}
          program. The full course covers everything from how the luxury travel industry works, to building your client base, selling confidently to affluent travelers, structuring your business, and much more.
        </p>
        <div className="bg-navy-800/50 rounded-lg p-5 mb-5 border border-navy-700">
          <p className="font-body text-navy-300 mb-1">
            <span className="line-through">Regular price: $1,597</span>
          </p>
          <p className="font-display text-2xl text-coral-400">
            Special price for Lead Program members: $297
          </p>
          <p className="font-body text-navy-300 text-sm mt-1">
            Full program access, including all modules and resources
          </p>
        </div>
        <a
          href="https://join.thedeolixacademy.com/luxury-travel-advisor"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 bg-coral-500 hover:bg-coral-600 text-white px-6 py-3 rounded-lg font-medium transition-colors"
        >
          Learn More About the Full Program
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
          </svg>
        </a>
      </div>

    </div>
  );
}
